/*
 * Standalone HMI runtime: one project, full screen, no app frame, no login
 * (docs/hmi-panel-ausbau.md §7). Runs on a gateway (served by the field
 * client), on a sinnux panel in the kiosk browser, or in any browser in the
 * LAN.
 *
 * URL parameters:
 *   quelle=lokal   (default) project, metadata and values from the field
 *                  client on the same origin
 *   quelle=demo    built-in showcase (EWP 10 Pro) against a simulator;
 *                  with projekt=rvs the LWP project with still values
 *   ziel=<target>  force a layout target, e.g. panelPortrait800x1280
 *   api=<path>     base of the local API (default api/v1/)
 *
 * The screen shown is in the hash (#heizen), so a kiosk can start on a
 * given screen and the browser's back button works.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import type { HmiNote, HmiScreen, ScreenLayout, WidgetDefinition } from '../hmi/widgets';
import { NotesOverlay } from './NotesOverlay';
import { RemoteServiceBanner } from './RemoteServiceBanner';
import type { IPointBindingSource } from '../hmi/binding';
import type { HmiLeafMeta } from '../hmi/leafMeta';
import { PointBindingSourceProvider } from '../hmi/PointBindingProvider';
import { LeafMetaLookupProvider } from '../hmi/LeafMetaContext';
import { WidgetHost } from '../hmi/WidgetHost';
import { GRID_SPECS } from '../hmi/editor/editorModel';
import { FreeStage } from '../hmi/panel/FreeStage';
import { ThemeScope, useEffectiveScheme, useStoredScheme } from '../hmi/panel/ThemeScope';
import { HmiRuntimeProvider, type HmiRuntime, type PanelInvitation } from '../hmi/panel/runtimeContext';
import { pickTarget } from './targetPicker';
import { LocalSource } from './LocalSource';
import { withSimulatedPoints } from '../hmi/simPoints';
import '../hmi/hmiCell.css';
import './runtime.css';

interface ProjectBody {
  appearance?: unknown;
  notes?: HmiNote[];
  widgets: WidgetDefinition[];
  screens: HmiScreen[];
}

interface Loaded {
  name: string;
  /** Fingerprint of the project as the client served it; null for the demo. */
  stamp: string | null;
  body: ProjectBody;
  source: IPointBindingSource;
  meta: Map<string, HmiLeafMeta>;
  local: LocalSource | null;
}

const params = new URLSearchParams(location.search);
const QUELLE = params.get('quelle') ?? 'lokal';
const ZIEL = params.get('ziel');
const API = params.get('api') ?? 'api/v1/';

async function load(): Promise<Loaded> {
  if (QUELLE === 'demo' && params.get('projekt') === 'rvs') {
    const [{ RVS_DEMO_BODY }, sim] = await Promise.all([import('./demo/rvsDemo'), import('./demo/rvsSim')]);
    return {
      name: 'LWP mit RVS21 (Vorführung)',
      body: RVS_DEMO_BODY as unknown as ProjectBody,
      source: withSimulatedPoints(sim.createRvsSim()),
      meta: sim.rvsDemoMeta(),
      local: null,
      stamp: null,
    };
  }
  if (QUELLE === 'demo') {
    const [{ EWP_DEMO_BODY }, sim] = await Promise.all([import('./demo/ewpDemo'), import('./demo/ewpSim')]);
    return {
      name: 'EWP 10 Pro (Vorführung)',
      body: EWP_DEMO_BODY as unknown as ProjectBody,
      source: sim.createEwpSim(),
      meta: sim.ewpDemoMeta(),
      local: null,
      stamp: null,
    };
  }
  const base = API.endsWith('/') ? API : `${API}/`;
  const resp = await fetch(`${base}hmi/project`, { cache: 'no-store' });
  if (resp.status === 404) throw new Error('Für dieses Gerät ist noch kein HMI-Projekt freigegeben.');
  if (!resp.ok) throw new Error(`Projekt nicht ladbar (HTTP ${resp.status}).`);
  const text = await resp.text();
  const project = JSON.parse(text) as { name?: string; body?: unknown; bodyJson?: string };
  const body = (project.body ?? JSON.parse(project.bodyJson ?? '{}')) as ProjectBody;
  const meta = new Map<string, HmiLeafMeta>();
  let metaText = '';
  try {
    const m = await fetch(`${base}hmi/meta`, { cache: 'no-store' });
    if (m.ok) {
      metaText = await m.text();
      const j = JSON.parse(metaText) as { points?: Record<string, HmiLeafMeta> };
      for (const [k, v] of Object.entries(j.points ?? {})) meta.set(k.toLowerCase(), v);
    }
  } catch { /* names and units then come without metadata */ }
  const local = new LocalSource(base);
  local.start();
  // "sim/..." sind Demowerte fuer das, was die Anlage nicht misst (simPoints.ts).
  return { name: project.name ?? 'HMI', stamp: fingerprint(text + metaText), body, source: withSimulatedPoints(local), meta, local };
}

/** Short hash of the served project. Revision alone is not enough: a gateway
 *  moved to another plant gets that plant's project, often also revision 1. */
function fingerprint(text: string): string {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `${text.length}-${(h >>> 0).toString(16)}`;
}

/** Reload the page when the client starts serving a different project, so a
 *  kiosk follows a new revision or a move to another plant without anyone
 *  restarting the browser. Checks every 30 s against the local client. */
function useReloadOnProjectChange(stamp: string | null): void {
  useEffect(() => {
    if (!stamp) return;
    const base = API.endsWith('/') ? API : `${API}/`;
    const id = window.setInterval(() => {
      // Projekt und Beschriftungen zusammen: eine geaenderte Einheit oder ein
      // neuer Name soll ebenso ankommen wie ein neues Bild.
      Promise.all([
        fetch(`${base}hmi/project`, { cache: 'no-store' }).then((r) => (r.ok ? r.text() : null)),
        fetch(`${base}hmi/meta`, { cache: 'no-store' }).then((r) => (r.ok ? r.text() : '')).catch(() => ''),
      ])
        .then(([t, m]) => { if (t && fingerprint(t + m) !== stamp) location.reload(); })
        .catch(() => { /* Client kurz weg: beim naechsten Mal wieder fragen */ });
    }, 30_000);
    return () => window.clearInterval(id);
  }, [stamp]);
}

export function useViewport(): { width: number; height: number } {
  const read = () => ({ width: window.innerWidth, height: window.innerHeight });
  const [vp, setVp] = useState(read);
  useEffect(() => {
    const on = () => setVp(read());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return vp;
}

export function useHashScreen(screens: readonly HmiScreen[]): [string | null, (id: string) => void] {
  const read = () => decodeURIComponent(location.hash.replace(/^#\/?/, '')) || null;
  const [id, setId] = useState<string | null>(read);
  useEffect(() => {
    const on = () => setId(read());
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  const valid = id && screens.some((s) => s.id === id) ? id : screens[0]?.id ?? null;
  return [valid, (next) => { location.hash = `#${encodeURIComponent(next)}`; }];
}

export function RuntimeApp() {
  const [state, setState] = useState<{ ok: true; data: Loaded } | { ok: false; error: string } | null>(null);
  const [online, setOnline] = useState(true);

  useEffect(() => {
    load().then(
      (data) => {
        setState({ ok: true, data });
        if (data.local) data.local.onConnection = setOnline;
      },
      (e: unknown) => setState({ ok: false, error: e instanceof Error ? e.message : String(e) }),
    );
  }, []);

  // Ohne Projekt beim Start nicht stehen bleiben: alle 30 s neu versuchen,
  // sonst braucht ein Panel, das vor der ersten Freigabe hochkam, jemanden
  // vor Ort (16.10.2026).
  useEffect(() => {
    if (!state || state.ok || QUELLE === 'demo') return undefined;
    const id = window.setTimeout(() => location.reload(), 30_000);
    return () => window.clearTimeout(id);
  }, [state]);

  if (!state) return <div class="hv-rt-message">HMI wird geladen …</div>;
  if (!state.ok) return <div class="hv-rt-message hv-rt-message--error">{state.error}<br />Neuer Versuch in 30 Sekunden.</div>;
  return <Runtime data={state.data} online={online} />;
}

function Runtime({ data, online }: { data: Loaded; online: boolean }) {
  const { body, source, meta } = data;
  const widgetsById = useMemo(() => new Map(body.widgets.map((w) => [w.id, w])), [body]);
  const [screenId, navigate] = useHashScreen(body.screens);
  const viewport = useViewport();
  const screen = body.screens.find((s) => s.id === screenId) ?? null;
  const target = screen ? pickTarget(screen.layouts.map((l) => l.target), viewport, ZIEL) : null;
  const layout = screen?.layouts.find((l) => l.target === target) ?? null;

  useEffect(() => { document.title = data.name; }, [data.name]);
  useReloadOnProjectChange(data.stamp);
  const [stored, setScheme] = useStoredScheme('hmi-scheme');
  const scheme = useEffectiveScheme(body.appearance, stored);

  const runtime: HmiRuntime = {
    screens: body.screens.map((s) => ({ id: s.id, label: s.label, iconRef: s.iconRef })),
    currentScreenId: screenId,
    navigate,
    assetUrl: (id) => QUELLE === 'demo' ? '' : `${API}hmi/assets/${encodeURIComponent(id)}`,
    editing: false,
    scheme,
    setScheme,
    // QR-Code "Benutzer hinzufuegen": nur gegen den eigenen Field-Client, der
    // die Einladung mit seinem Geraetezertifikat beim Server holt. Der Client
    // beantwortet das nur von 127.0.0.1 (HmiServer).
    createInvitation: data.local ? () => fetchInvitation(API.endsWith('/') ? API : `${API}/`) : undefined,
  };
  const lookup = useMemo(() => (key: string | null | undefined) => (key ? meta.get(key.toLowerCase()) : undefined), [meta]);

  return (
    <ThemeScope appearance={body.appearance} scheme={stored} class="hv-rt">
      <HmiRuntimeProvider value={runtime}>
        <PointBindingSourceProvider source={source}>
          <LeafMetaLookupProvider lookup={lookup}>
            {!screen && <div class="hv-rt-message">Das Projekt hat keine Bildschirme.</div>}
            {screen && layout && (layout.mode === 'free'
              ? <FreeStage key={`${screen.id}-${layout.target}`} layout={layout} widgetIds={screen.widgetIds}
                           widgetsById={widgetsById} fit="contain" />
              : <GridScreen layout={layout} widgetIds={screen.widgetIds} widgetsById={widgetsById} />)}
            {screen && !layout && <div class="hv-rt-message">Dieser Bildschirm hat keine Anordnung.</div>}
          </LeafMetaLookupProvider>
        </PointBindingSourceProvider>
      </HmiRuntimeProvider>
      {!online && <div class="hv-rt-offline" role="status">Keine Verbindung zum Gateway</div>}
      <RemoteServiceBanner api={data.local ? (API.endsWith('/') ? API : `${API}/`) : null} />
      <NotesOverlay notes={body.notes} api={data.local ? (API.endsWith('/') ? API : `${API}/`) : null} />
    </ThemeScope>
  );
}

async function fetchInvitation(base: string): Promise<PanelInvitation> {
  const resp = await fetch(`${base}portal/invitation`, { method: 'POST', cache: 'no-store' });
  let body: { url?: string; expiresAt?: string; error?: string } = {};
  try { body = await resp.json(); } catch { /* leer */ }
  if (!resp.ok || !body.url || !body.expiresAt) {
    throw new Error(body.error ?? 'Der QR-Code ließ sich gerade nicht holen.');
  }
  return { url: body.url, expiresAt: body.expiresAt };
}

/** Grid layouts (projects drawn before free layout existed). */
export function GridScreen({ layout, widgetIds, widgetsById }: {
  layout: ScreenLayout; widgetIds: readonly string[]; widgetsById: Map<string, WidgetDefinition>;
}) {
  const spec = GRID_SPECS[layout.target];
  const columns = layout.canvasSize?.columns ?? spec.columns;
  return (
    <div class="hv-rt-grid" style={{
      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      gridAutoRows: `${spec.cellHeightPx}px`,
    }}>
      {widgetIds.map((id) => {
        const w = widgetsById.get(id);
        const p = layout.placements[id];
        if (!w || !p) return null;
        return (
          <div key={id} class="hv-hmi-cell" style={{
            gridColumn: `${p.col + 1} / span ${Math.max(1, p.colSpan)}`,
            gridRow: `${p.row + 1} / span ${Math.max(1, p.rowSpan)}`,
          }}>
            <WidgetHost widget={w} />
          </div>
        );
      })}
    </div>
  );
}
